import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import Pagination from '../../model/pagination';
import CooperationModel from '../../model/cooperation.model';
import {environment} from '../../environments/environment';
import QueryEncoder from '../queryEncoder';
import InvoiceModel from '../../model/invoice.model';
import RevenueModel from '../../model/revenue.model';
import {switchMap} from "rxjs/operators";
import moment from "moment";
import BillingCycleModel from "../../model/billingCycle.model";

@Injectable()
export default class InvoiceService {
  public constructor(private http: HttpClient) {
  }

  public list(query?: string, page?: number, limit?: number, sort?: string, direction?: string, additionalInfo?: any): Observable<Pagination<InvoiceModel>> {
    query = query || '';
    page = page || 1;
    limit = limit || 15;
    sort = sort || '';

    let sorting = {};
    sorting[sort] = direction;

    let q = {query, page, limit, sorting};


    if(additionalInfo) {
      Object.assign(q, additionalInfo);
    }

    return this.http
      .get<Pagination<InvoiceModel>>(environment.apiUrl + '/invoices?' + QueryEncoder.encodeURI(q));
  }

  public bulkDownload(query?: string, additionalInfo?: any): Observable<Blob> {
    query = query || '';


    let q = {query};


    if(additionalInfo) {
      Object.assign(q, additionalInfo);
    }

    return this.http.get<Blob>(environment.apiUrl + '/invoices/bulk-download?' + QueryEncoder.encodeURI(q),{
          observe: 'response',
          responseType: 'arraybuffer' as 'json'
        }).pipe(switchMap((result: any) => {
          const blob = new Blob([result.body], { type: 'application/pdf' });
          return of(blob);
        }));
  }

  public show(id: number): Observable<InvoiceModel> {
    return this.http.get<InvoiceModel>(environment.apiUrl + '/invoices/' + id);
  }

  public delete(invoice: InvoiceModel): Observable<any> {
    return this.http.delete(environment.apiUrl + '/invoices/' + invoice.id);
  }

  public update(invoice: InvoiceModel): Observable<any> {
    return this.http.put(environment.apiUrl + '/invoices/' + invoice.id, invoice);
  }

  public create(invoice: InvoiceModel): Observable<InvoiceModel> {
    return this.http.post<InvoiceModel>(environment.apiUrl + '/invoices', invoice);
  }

  public cancel(invoice: InvoiceModel): Observable<InvoiceModel> {
    return this.http.post<InvoiceModel>(environment.apiUrl + '/invoices/' + invoice.id + '/cancel', {});
  }

  public createGroup(revenues: RevenueModel[], billingCycle: BillingCycleModel, showOnlySum?: boolean): Observable<InvoiceModel[]> {
    let ids = [];
    for(let revenue of revenues) {
      ids.push(revenue.id);
    }

    showOnlySum = showOnlySum || false;

    return this.http.post<InvoiceModel[]>(environment.apiUrl + '/invoices/create-grouped', {
      revenue_ids: ids,
      show_only_sum: showOnlySum,
      billing_cycle_id: billingCycle.id
    });
  }

  public bulkCreate(revenues: RevenueModel[]): Observable<InvoiceModel[]> {
    let ids = [];
    for(let revenue of revenues) {
      ids.push(revenue.id);
    }

    return this.http.post<InvoiceModel[]>(environment.apiUrl + '/invoices/bulk-create', {
      revenue_ids: ids
    });
  }

  public exportCsv(from: Date, to: Date, companyId: number) {
    let q = {
      from: moment(from).format('YYYY-MM-DD'),
      to: moment(to).format('YYYY-MM-DD'),
      company: companyId
    };
    return this.http.get<Blob>(environment.apiUrl + '/invoices/csv-export?' + QueryEncoder.encodeURI(q),{
      observe: 'response',
      responseType: 'arraybuffer' as 'json'
    }).pipe(switchMap((result: any) => {
      const blob = new Blob([result.body], { type: 'text/csv' });
      return of(blob);
    }));
  }
}
